import type React from "react";
import { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
  fetchShipments,
  fetchDriverGroups,
  fetchDriverGroupById,
  clearError,
  clearCurrentGroup,
} from "../../store/slices/driverSlice";
import {
  MapPin,
  Package,
  LogOut,
  ChevronRight,
  Package2,
  WeightIcon,
  ArrowLeft,
  Layers,
  AlertTriangle,
  Home,
} from "lucide-react";
import DriverReclamationModal from "../../components/shipments/DriverReclamationModal";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { displayCargoNumber } from "../../utils/cargoDisplay";
import { vehicleLabel } from "../../utils/vehicleLabel";

export const DriversShipments: React.FC = () => {
  const { t } = useTranslation("shipments");
  const { user, logout } = useAuth();
  const dispatch = useAppDispatch();
  const { shipments, groups, currentGroup, loading, error } = useAppSelector(
    (state) => state.driver,
  );
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"active" | "completed">("active");
  const [reclamationShipment, setReclamationShipment] = useState<{ _id: string; shipmentNumber: string } | null>(null);

  useEffect(() => {
    const params = { page: 1, limit: 1000 };
    dispatch(fetchShipments(params));
    dispatch(fetchDriverGroups(params));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleViewGroup = (groupId: string) => {
    dispatch(fetchDriverGroupById(groupId));
  };

  const handleBackToList = () => {
    dispatch(clearCurrentGroup());
  };

  const filteredGroups = groups?.filter((group) => {
    const allCompleted = group.shipments?.every(
      (s: any) => s.status === "Completed",
    );
    return activeTab === "active" ? !allCompleted : allCompleted;
  });

  const filteredShipments = shipments?.filter((shipment) => {
    const isCompleted = shipment.status === "Completed";
    return activeTab === "active" ? !isCompleted : isCompleted;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ReadyForPickup":
        return "bg-purple-100 text-purple-700";
      case "AwaitingPickup":
        return "bg-orange-100 text-orange-800";
      case "InTransit":
        return "bg-blue-100 text-blue-800";
      case "Completed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const STATUS_ORDER: Record<string, number> = {
    Order: 0,
    ReadyForPickup: 1,
    AwaitingPickup: 2,
    InTransit: 3,
    Completed: 4,
  };

  const getGroupProgressText = (group: any) => {
    const shipments = group.shipments || [];
    const total = shipments.length;

    if (total === 0) return t("driver.noShipments");

    // Find furthest status reached
    let maxStatusLevel = 0;

    shipments.forEach((s: any) => {
      const level = STATUS_ORDER[s.status] ?? 0;
      if (level > maxStatusLevel) {
        maxStatusLevel = level;
      }
    });

    // Count shipments that reached AT LEAST that level
    const reachedCount = shipments.filter(
      (s: any) => (STATUS_ORDER[s.status] ?? 0) >= maxStatusLevel,
    ).length;

    switch (maxStatusLevel) {
      case STATUS_ORDER.Completed:
        return `${reachedCount}/${total} ${t("driver.completed")}`;

      case STATUS_ORDER.InTransit:
        return `${reachedCount}/${total} ${t("driver.inTransit")}`;

      case STATUS_ORDER.AwaitingPickup:
        return `${reachedCount}/${total} ${t("driver.pickedUp")}`;

      case STATUS_ORDER.ReadyForPickup:
        return t("driver.readyForPickup");

      default:
        return t("status.Order");
    }
  };

  if (loading && !currentGroup && groups.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (currentGroup) {
    return (
      <>
      <div className="min-h-screen bg-gray-50">
        <div className="pt-safe bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToList}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {t("driver.groupTitle")} {currentGroup.groupNumber}
                </h1>
                <p className="text-xs text-gray-500">
                  {t("driver.truck")}{" "}
                  {vehicleLabel(currentGroup?.truckLicensePlate) || "-"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-gray-900">
                {t("driver.shipments")}
              </p>
              <p className="text-xs text-gray-500">
                {getGroupProgressText(currentGroup)}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {currentGroup.shipments?.map((shipment: any) => (
              <ShipmentCard
                key={shipment._id}
                shipment={shipment}
                onAction={() => navigate(`/driver/${shipment._id}`)}
                onReclaim={() => setReclamationShipment({ _id: shipment._id, shipmentNumber: shipment.shipmentNumber })}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        </div>
      </div>
      {reclamationShipment && (
        <DriverReclamationModal
          tokenOrId={reclamationShipment._id}
          shipmentNumber={reclamationShipment.shipmentNumber}
          onClose={() => setReclamationShipment(null)}
          onSuccess={() => setReclamationShipment(null)}
        />
      )}
      </>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      {/* Header code stays same ... */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold uppercase">
              {user?.firstName?.[0] || "D"}
            </div>
            <span className="text-sm font-medium text-gray-700">
              {user?.fullName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/driver-home")}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Home className="h-4 w-4 sm:mr-2" />{" "}
              <span className="hidden sm:inline">{t("driver.home")}</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />{" "}
              <span className="hidden sm:inline">{t("driver.logout")}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {t("driver.yourAssignments")}
          </h2>
          <p className="text-sm text-gray-600">
            {filteredGroups.length} {t("driver.group")} •{" "}
            {filteredShipments.length} {t("driver.individualShipments")}
          </p>
        </div>

        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {(["active", "completed"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {t(`driver.${tab}`)}
              </button>
            ))}
          </nav>
        </div>

        <section className="mb-12">
          <div className="flex items-center mb-6 text-gray-700">
            <Layers className="h-5 w-5 mr-2" />
            <h3 className="text-lg font-semibold">
              {t("driver.routeGroups")} ({filteredGroups.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredGroups.map((group: any) => (
              <div
                key={group._id}
                className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-bold">{group.groupNumber}</h4>
                  <span className="text-xs text-gray-500">
                    {getGroupProgressText(group)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <div className="flex items-center mb-2">
                      <MapPin className="shrink-0 h-4 w-4 text-green-500 mr-1" />
                      <p className="text-sm font-medium text-gray-900">
                        {t("driver.pickup")}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">
                      {t("driver.multipleLocations")}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center justify-end mb-2">
                      <p className="text-sm font-medium text-gray-900">
                        {t("driver.delivery")}
                      </p>
                      <MapPin className="shrink-0 h-4 w-4 text-red-500 ml-1" />
                    </div>
                    <p className="text-xs text-gray-500 mb-1 text-right">
                      {t("driver.multipleLocations")}
                    </p>
                  </div>
                </div>
                <div className="flex justify-between items-end text-xs text-gray-500">
                  <div className="flex items-center">
                    <Package2 className="h-3 w-3 mr-1" />
                    {group?.shipments?.reduce(
                      (sum: any, shipment: any) =>
                        sum + (shipment.totalQuantity || 0),
                      0,
                    )}
                  </div>
                  <div className="flex justify-between gap-3">
                    <div className="flex items-center pr-3">
                      <WeightIcon className="h-3 w-3 mr-1" />
                      {group?.shipments?.reduce(
                        (sum: any, shipment: any) =>
                          sum + (shipment.totalWeight || 0),
                        0,
                      )}{" "}
                      kg
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewGroup(group._id)}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md flex items-center hover:bg-blue-700 transition-colors"
                  >
                    {t("driver.view")} <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center mb-6 text-gray-700">
            <Package className="h-5 w-5 mr-2" />
            <h3 className="text-lg font-semibold">
              {t("driver.individualShipments")} ({filteredShipments.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredShipments.map((shipment) => (
              <ShipmentCard
                key={shipment._id}
                shipment={shipment}
                onAction={() => navigate(`/driver/${shipment._id}`)}
                onReclaim={() => setReclamationShipment({ _id: shipment._id, shipmentNumber: shipment.shipmentNumber })}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
    {reclamationShipment && (
      <DriverReclamationModal
        tokenOrId={reclamationShipment._id}
        shipmentNumber={reclamationShipment.shipmentNumber}
        onClose={() => setReclamationShipment(null)}
        onSuccess={() => setReclamationShipment(null)}
      />
    )}
    </>
  );
};

const ShipmentCard = ({ shipment, onAction, onReclaim, getStatusColor }: any) => {
  const { t } = useTranslation("shipments");
  const getCountryName = (country: any): string => {
    if (!country) return "-";
    if (typeof country === "object" && country !== null)
      return country.name || "-";
    return "-"; // raw ObjectId string — no name to show
  };

  const safeFormatDate = (value: any): string => {
    if (!value) return "-";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-GB");
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900">
          {shipment.shipmentNumber}{" "}
          {shipment.referenceNumber && (
            <span className="text-sm font-normal text-gray-600">
              ({shipment.referenceNumber})
            </span>
          )}
        </h3>
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
            shipment.status,
          )}`}
        >
          {shipment.status === "AwaitingPickup"
            ? t("driver.pickedUp")
            : shipment.status}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-center mb-2">
            <MapPin className="shrink-0 h-4 w-4 text-green-500 mr-1" />
            <p className="text-sm font-medium text-gray-900">
              {t("driver.pickup")}
            </p>
          </div>
          <p className="text-sm text-gray-600 mb-1">
            {shipment?.pickup?.companyName}
          </p>
          <p className="text-xs text-gray-500 mb-1">
            {shipment?.pickup?.address?.city || "-"},{" "}
            {getCountryName(shipment?.pickup?.address?.country)}
          </p>
          <p className="text-xs text-gray-500">
            {safeFormatDate(shipment?.pickup?.date)} -{" "}
            {shipment?.pickup?.time || "-"}
          </p>
        </div>

        <div className="text-right">
          <div className="flex items-center justify-end mb-2">
            <p className="text-sm font-medium text-gray-900">
              {t("driver.delivery")}
            </p>
            <MapPin className="shrink-0 h-4 w-4 text-red-500 ml-1" />
          </div>
          <p className="text-sm text-gray-600 mb-1">
            {shipment?.delivery?.companyName}
          </p>
          <p className="text-xs text-gray-500 mb-1">
            {shipment?.delivery?.address?.city || "-"},{" "}
            {getCountryName(shipment?.delivery?.address?.country)}
          </p>
          <p className="text-xs text-gray-500">
            {safeFormatDate(shipment?.delivery?.date)} -{" "}
            {shipment.delivery?.time || "-"}
          </p>
        </div>
      </div>
      <div className="mt-6 flex justify-between items-end">
        <div className="flex flex-col space-y-1 text-xs text-gray-500">
          <div className="flex items-center">
            <Package2 className="h-3 w-3 mr-1" />
            {displayCargoNumber(shipment?.totalQuantity)}
          </div>
          <div className="flex justify-between gap-3">
            <div className="flex items-center pr-3 border-r border-gray-300">
              <WeightIcon className="h-3 w-3 mr-1" />
              {displayCargoNumber(shipment?.totalWeight)}
            </div>
            <div className="flex items-center">
              <Package className="h-3 w-3 mr-1" />
              {shipment?.loadType || "N/A"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onReclaim}
            className="px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 text-sm font-medium rounded-md flex items-center gap-1.5 hover:bg-amber-100"
            title="File a reclamation for this shipment"
          >
            <AlertTriangle className="h-4 w-4" />
            Reclaim
          </button>
          <button
            onClick={onAction}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md flex items-center hover:bg-blue-700"
          >
            {t("driver.view")} <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};
